import {txt, type Texto} from '../i18n/idioma';

// Nota de cada campo de Cashflow (Daniel, 14/10/2026): qué es y cómo se llena.
// "Payment Schedule" = el Excel Payment Schedule Third Parties (hojas Input y
// Payment Schedule); las financiaciones son las de Exportaciones → Financiaciones.

// ── Movimientos (y "Vencido y próximos 30 días" del Resumen) ──
export const AYUDA_MOVIMIENTOS: Record<string, Texto> = {
  exportacion: txt(
    'Exportación a la que pertenece el movimiento. Sale del pago a tercero (columna Export del Payment Schedule) o de la financiación de esa exportación.',
    'Export the movement belongs to. It comes from the third-party payment (Export column of the Payment Schedule) or from that export’s financing.',
  ),
  fecha: txt(
    'Cuándo se hizo o se espera el movimiento. Hecho: la fecha real registrada. Por hacer: la fecha meta del pago o la que calcula la app con el seguimiento (desembolso el día del zarpe; pago al financiador unos días hábiles antes de la ETA a Mundra).',
    'When the movement was made or is expected. Done: the actual date recorded. To do: the payment target date or the one the app works out from the tracking (disbursement on the departure day; payment to the financer some business days before the Mundra ETA).',
  ),
  estado: txt(
    'Realizado: ya se hizo. Vencido: no se ha hecho y su fecha ya pasó. Previsto: por hacer, con fecha de hoy en adelante o sin fecha. Lo calcula la app con la fecha de hoy.',
    'Done: already made. Overdue: not made and its date has passed. Expected: still to do, dated today or later or undated. The app works it out with today’s date.',
  ),
  concepto: txt(
    'Qué movimiento es: pago a un proveedor tercero (con su tipo: anticipo, liquidación…), desembolso del financiador, saldo, anticipo al financiador, pago del comprador o del cliente final. Lo arma la app con los pagos a terceros y las financiaciones.',
    'Which movement it is: payment to a third-party supplier (with its type: advance, settlement…), financer disbursement, balance, advance to the financer, buyer or final customer payment. The app builds it from third-party payments and financings.',
  ),
  origen: txt(
    'Clase de movimiento, sin el detalle: pago a proveedor tercero, desembolso, saldo, anticipo, pago del comprador o del cliente final. Los pagos a terceros salen de la pestaña Pagos a terceros; los demás, de la financiación de la exportación.',
    'Kind of movement, without the detail: payment to third-party supplier, disbursement, balance, advance, buyer or final customer payment. Third-party payments come from the Third-party payments tab; the others from the export’s financing.',
  ),
  paga: txt(
    'Empresa que entrega el dinero. En un pago a tercero es la empresa del grupo (Buyer) o, si el monto es negativo, el proveedor que devuelve; en una financiación, el financiador o el comprador según el movimiento.',
    'Company that hands over the money. In a third-party payment it is the group company (Buyer) or, if the amount is negative, the supplier paying back; in a financing, the financer or the buyer depending on the movement.',
  ),
  recibe: txt(
    'Empresa que recibe el dinero: el proveedor en un pago a tercero (o la empresa del grupo si le devuelven); en un desembolso, la empresa que está antes del financiador en la cadena de la exportación; en un pago del comprador, el financiador o quien vende.',
    'Company that receives the money: the supplier in a third-party payment (or the group company when paid back); in a disbursement, the company just before the financer in the export chain; in a buyer payment, the financer or the seller.',
  ),
  montoUsd: txt(
    'Valor del movimiento en dólares, siempre positivo (el sentido lo dan Paga y Recibe). Sale del monto del pago (columna Amount) o de los valores de la financiación (financiado, factura al comprador, saldo).',
    'Amount of the movement in dollars, always positive (Pays and Receives give the direction). It comes from the payment amount (Amount column) or from the financing values (financed, buyer invoice, balance).',
  ),
  linea: txt(
    'Línea de financiación por la que se mueve el dinero (BLQ, BLQM, LC…). En un pago a tercero es la del pago o, si no tiene, la de su acuerdo; en una financiación, la del financiador. Vacía en la venta directa y en el pago del cliente final.',
    'Financing line the money moves through (BLQ, BLQM, LC…). In a third-party payment it is the payment’s own or, if none, its agreement’s; in a financing, the financer’s. Empty in a direct sale and in the final customer payment.',
  ),
};

// ── En proceso: lo que se está financiando ──
export const AYUDA_EN_PROCESO: Record<string, Texto> = {
  exportacion: txt(
    'Exportación con financiación abierta en una línea que financia: el comprador todavía no paga. Viene de Exportaciones → Financiaciones.',
    'Export with an open financing on a financing line: the buyer has not paid yet. It comes from Exports → Financings.',
  ),
  linea: txt(
    'Código del financiador de la exportación (BLQ, BLQM, LC…), registrado en su financiación.',
    'Code of the export’s financer (BLQ, BLQM, LC…), recorded in its financing.',
  ),
  estado: txt(
    'Financiada: ya ocupa el cupo (en BLQ, cuando ya está facturada al financiador con el pago inicial registrado). En proceso: todavía no ocupa el cupo. Por financiar: marcada así en la financiación. Lo calcula la app.',
    'Financed: it already uses the credit line (in BLQ, once invoiced to the financer with the initial payment recorded). In process: it does not use the line yet. To be financed: marked that way in the financing. The app works it out.',
  ),
  financiadoUsd: txt(
    'Monto financiado de la exportación, tal como está en su financiación (Status BLQ o lo escrito en la app).',
    'Financed amount of the export, as recorded in its financing (Status BLQ or what was written in the app).',
  ),
  recibe: txt(
    'Empresa que recibió (o recibirá) el desembolso: la que está justo antes del financiador en la cadena Intercompany o, si no aparece, la del prefijo de la factura.',
    'Company that received (or will receive) the disbursement: the one just before the financer in the Intercompany chain or, if missing, the one in the invoice prefix.',
  ),
  desembolso: txt(
    'Fecha del desembolso. Si la financiación no la tiene, la esperada: en BLQ y BLQM, el día del zarpe (real o ETD) del seguimiento; en las demás líneas, la del archivo. "(esperado)" = todavía no se ha recibido.',
    'Disbursement date. If the financing does not have it, the expected one: in BLQ and BLQM, the departure day (actual or ETD) from the tracking; in other lines, the one in the file. "(expected)" = not received yet.',
  ),
  diasFinanciado: txt(
    'Días que lleva financiada hasta hoy: desde el desembolso real o, si ya ocupa el cupo, desde el zarpe. Lo calcula la app.',
    'Days it has been financed until today: from the actual disbursement or, if it already uses the line, from departure. The app works it out.',
  ),
  comprador: txt(
    'Empresa que le paga al financiador: la que sigue después de él en la cadena Intercompany (o el comprador del archivo).',
    'Company that pays the financer: the one right after it in the Intercompany chain (or the buyer in the file).',
  ),
  pagoComprador: txt(
    'Fecha esperada del pago al financiador. En BLQ, 5 días hábiles antes de la ETA a Mundra del seguimiento (3 en BLQM); sin esa ETA y en las demás líneas, la del archivo. En rojo si ya pasó.',
    'Expected date of the payment to the financer. In BLQ, 5 business days before the Mundra ETA in the tracking (3 in BLQM); without that ETA and in other lines, the one in the file. In red if it has passed.',
  ),
  pendienteTercerosUsd: txt(
    'Suma de los pagos a proveedores terceros de esta exportación que siguen pendientes o solicitados, sin las devoluciones.',
    'Sum of this export’s third-party supplier payments still pending or requested, without paybacks.',
  ),
};

// ── Pagos a terceros ──
export const AYUDA_PAGOS: Record<string, Texto> = {
  exportacion: txt(
    'Exportación a la que corresponde el pago. Columna Export de la hoja Payment Schedule, o lo escrito al registrar el pago. Con * = todavía no está en la app.',
    'Export the payment belongs to. Export column of the Payment Schedule sheet, or what was written when registering the payment. With * = not in the app yet.',
  ),
  comprador: txt(
    'Empresa del grupo que le paga al proveedor. Columna Buyer de la hoja Payment Schedule, o lo escrito al registrar el pago.',
    'Group company that pays the supplier. Buyer column of the Payment Schedule sheet, or what was written when registering the payment.',
  ),
  proveedor: txt(
    'Proveedor tercero que recibe el pago. Columna Supplier de la hoja Payment Schedule.',
    'Third-party supplier that receives the payment. Supplier column of the Payment Schedule sheet.',
  ),
  tipo: txt(
    'Anticipo, liquidación, ajuste, devolución… Columna Payment type de la hoja Payment Schedule; un tipo que la app no conoce entra como Ajuste.',
    'Advance, settlement, adjustment, repayment… Payment type column of the Payment Schedule sheet; a type the app does not know is loaded as Adjustment.',
  ),
  montoUsd: txt(
    'Valor del pago en dólares. Columna Amount; negativo = el proveedor devuelve. Se corrige con ✎ Editar.',
    'Payment amount in dollars. Amount column; negative = the supplier pays back. Fixed with ✎ Edit.',
  ),
  llegada: txt(
    'Llegada de la exportación: la ETA (o la llegada real) del seguimiento de la app; si la exportación no está en la app, la ETA del archivo.',
    'Arrival of the export: the ETA (or actual arrival) from the app tracking; if the export is not in the app, the ETA in the file.',
  ),
  fechaMeta: txt(
    'Fecha en que debe hacerse el pago. Columna Payment target date; si el seguimiento tiene otra ETA a Mundra, la app la mueve los mismos días. Un pago editado en la app ya no se mueve.',
    'Date the payment is due. Payment target date column; if the tracking has another Mundra ETA, the app moves it by the same days. A payment edited in the app no longer moves.',
  ),
  estado: txt(
    'Pendiente, Solicitado, Pagado o Cancelado. Columna Status del archivo; se cambia con ✓ Pagado o ✎ Editar. Un pago cancelado no cuenta en el Cashflow.',
    'Pending, Requested, Paid or Cancelled. Status column of the file; changed with ✓ Paid or ✎ Edit. A cancelled payment does not count in the Cashflow.',
  ),
  pagadoEl: txt(
    'Fecha en que se hizo el pago. Columna Payment date; con el botón ✓ Pagado queda la fecha de hoy.',
    'Date the payment was made. Payment date column; with the ✓ Paid button it gets today’s date.',
  ),
  linea: txt(
    'Línea de financiación con que se paga: la del pago (elegida con ✎ Editar) o, si no tiene, la de su acuerdo (marcada "acuerdo"). Con línea, lo pendiente se descuenta del cupo en el Resumen.',
    'Financing line it is paid with: the payment’s own (chosen with ✎ Edit) or, if none, its agreement’s (marked "agreement"). With a line, what is pending is deducted from the credit line in the Summary.',
  ),
  acuerdo: txt(
    'Código del acuerdo con el proveedor. Columna Agreement ID; sus condiciones están en la pestaña Acuerdos.',
    'Code of the agreement with the supplier. Agreement ID column; its terms are in the Agreements tab.',
  ),
  comentarios: txt(
    'Notas del pago. Columna Comments del archivo; se cambian con ✎ Editar.',
    'Payment notes. Comments column of the file; changed with ✎ Edit.',
  ),
  acciones: txt(
    'Marcar el pago como pagado o editarlo. Lo editado en la app no lo cambia una nueva carga del Payment Schedule.',
    'Mark the payment as paid or edit it. What is edited in the app is not changed by a new Payment Schedule load.',
  ),
};

// ── Acuerdos (hoja Input) ──
export const AYUDA_ACUERDOS: Record<string, Texto> = {
  codigo: txt(
    'Código del acuerdo. Columna Agreement ID de la hoja Input del Payment Schedule.',
    'Agreement code. Agreement ID column of the Input sheet of the Payment Schedule.',
  ),
  nombre: txt(
    'Nombre del acuerdo. Columna Agreement de la hoja Input.',
    'Agreement name. Agreement column of the Input sheet.',
  ),
  comprador: txt(
    'Empresa del grupo que le compra al proveedor. Columna Buyer de la hoja Input.',
    'Group company that buys from the supplier. Buyer column of the Input sheet.',
  ),
  porcentajeAnticipo: txt(
    'Porcentaje que se le adelanta al proveedor. Columna Provisional payment % de la hoja Input.',
    'Percentage advanced to the supplier. Provisional payment % column of the Input sheet.',
  ),
  terminosAnticipo: txt(
    'Cuándo se paga el anticipo. Columnas Provisional payment terms, Days y Type of days (hábiles o calendario) de la hoja Input.',
    'When the advance is paid. Provisional payment terms, Days and Type of days (business or calendar) columns of the Input sheet.',
  ),
  qp: txt(
    'Periodo de cotización (QP) con que se fija el precio. Columna QP de la hoja Input.',
    'Quotational period (QP) used to set the price. QP column of the Input sheet.',
  ),
  terminosFinales: txt(
    'Cómo se liquida el saldo. Columna Final payment terms de la hoja Input.',
    'How the balance is settled. Final payment terms column of the Input sheet.',
  ),
  garantia: txt(
    'Garantía del acuerdo. Columna SBLC de la hoja Input.',
    'Agreement guarantee. SBLC column of the Input sheet.',
  ),
  pagos: txt(
    'Cuántos pagos a terceros tiene este acuerdo. Lo cuenta la app.',
    'How many third-party payments this agreement has. The app counts them.',
  ),
  pendienteUsd: txt(
    'Suma de sus pagos pendientes o solicitados. La calcula la app.',
    'Sum of its pending or requested payments. The app works it out.',
  ),
  linea: txt(
    'Línea de financiación con que se pagan los anticipos del acuerdo. No viene del archivo: se elige aquí; un pago puede tener otra.',
    'Financing line used to pay the agreement’s advances. It does not come from the file: it is chosen here; a payment can have another.',
  ),
};

// ── Lo que cada empresa tiene por recibir y por pagar ──
export const AYUDA_POR_EMPRESA: Record<string, Texto> = {
  empresa: txt(
    'Empresa que paga o recibe en los movimientos. Los financiadores no salen aquí: tienen sus tarjetas de cupo.',
    'Company that pays or receives in the movements. Financers are not listed here: they have their credit line cards.',
  ),
  entradasUsd: txt(
    'Lo que la empresa tiene por recibir: suma de sus movimientos previstos y vencidos en los que ella recibe, de las exportaciones del periodo elegido.',
    'What the company has to receive: sum of its expected and overdue movements in which it receives, for the exports of the chosen period.',
  ),
  vencidoEntradasUsd: txt(
    'La parte de lo por recibir cuya fecha ya pasó.',
    'The part to receive whose date has passed.',
  ),
  salidasUsd: txt(
    'Lo que la empresa tiene por pagar: suma de sus movimientos previstos y vencidos en los que ella paga.',
    'What the company has to pay: sum of its expected and overdue movements in which it pays.',
  ),
  vencidoSalidasUsd: txt('La parte de lo por pagar cuya fecha ya pasó.', 'The part to pay whose date has passed.'),
  netoUsd: txt(
    'Por recibir menos por pagar. Positivo: le entra más de lo que le sale.',
    'To receive minus to pay. Positive: more comes in than goes out.',
  ),
};

// ── Tarjeta de cada cupo en el Resumen ──
export const AYUDA_CUPO = {
  usado: txt(
    'Lo financiado que ocupa el cupo hoy: exportaciones de esta línea que el comprador todavía no paga (en BLQ, solo las ya facturadas al financiador con el pago inicial registrado). Sale de las financiaciones.',
    'Financed amount using the credit line today: exports on this line the buyer has not paid yet (in BLQ, only those already invoiced to the financer with the initial payment recorded). It comes from the financings.',
  ),
  cupo: txt(
    'Monto total de la línea. Se escribe en Configuración de campos → Financiadores; un cupo compartido es la suma de los dos.',
    'Total amount of the line. It is entered in Field settings → Financers; a shared credit line is the sum of both.',
  ),
  disponible: txt('Cupo menos lo usado hoy.', 'Credit line minus what is used today.'),
  compromisos: txt(
    'Pagos a proveedores terceros pendientes o solicitados que se pagan con esta línea (la del pago o la de su acuerdo), sin las devoluciones.',
    'Pending or requested third-party supplier payments paid with this line (the payment’s own or its agreement’s), without paybacks.',
  ),
  disponibleDespues: txt(
    'Lo disponible hoy menos los pagos a terceros por hacer. En rojo si no alcanza.',
    'Available today minus the third-party payments due. In red if it is not enough.',
  ),
  desembolsos: txt(
    'Desembolsos del financiador que todavía no se han recibido (previstos o vencidos).',
    'Financer disbursements not received yet (expected or overdue).',
  ),
  porCobrar: txt(
    'Lo que los compradores le deben a esta línea: valor de la factura al comprador (o lo financiado) de las financiaciones que el comprador aún no paga.',
    'What buyers owe this line: buyer invoice value (or financed amount) of the financings the buyer has not paid yet.',
  ),
  porEmpresa: txt(
    'Cómo se reparte lo usado: la empresa que recibió cada desembolso (la que está antes del financiador en la cadena).',
    'How the used amount is split: the company that received each disbursement (the one before the financer in the chain).',
  ),
};

// ── Filtros, totales y formularios ──
export const AYUDA_FILTROS_CASHFLOW = {
  anioEtd: txt(
    'Año de zarpe de la exportación: el zarpe real o la ETD del seguimiento; si no tiene, el que trae el código de la exportación.',
    'Departure year of the export: the actual departure or the ETD from the tracking; if it has none, the one in the export code.',
  ),
  mesEtd: txt(
    'Mes de zarpe de la exportación: el zarpe real o la ETD del seguimiento; si no tiene, el que trae el código de la exportación.',
    'Departure month of the export: the actual departure or the ETD from the tracking; if it has none, the one in the export code.',
  ),
  buscarMovimientos: txt(
    'Busca en la exportación, las empresas que pagan o reciben y el tipo de movimiento.',
    'Searches the export, the companies that pay or receive and the movement type.',
  ),
  empresa: txt(
    'Movimientos en los que la empresa paga o recibe. Con una empresa elegida se ven sus totales de lo que entra y sale.',
    'Movements in which the company pays or receives. With a company chosen, its money in and out totals are shown.',
  ),
  lineaMovimientos: txt(
    'Línea de financiación del movimiento (BLQ, BLQM, LC…) o "Sin línea".',
    'Financing line of the movement (BLQ, BLQM, LC…) or "No line".',
  ),
  desde: txt(
    'Solo movimientos con fecha desde este día. Los que no tienen fecha no salen.',
    'Only movements dated from this day. Undated ones are left out.',
  ),
  hasta: txt(
    'Solo movimientos con fecha hasta este día. Los que no tienen fecha no salen.',
    'Only movements dated up to this day. Undated ones are left out.',
  ),
  totalesEmpresa: txt(
    'Entra: suma de los movimientos filtrados en los que la empresa elegida recibe. Sale: en los que paga. Neto: Entra menos Sale.',
    'In: sum of the filtered movements in which the chosen company receives. Out: those in which it pays. Net: In minus Out.',
  ),
  total: txt('Suma de los montos de todos los movimientos filtrados.', 'Sum of the amounts of all filtered movements.'),
  totalEnProceso: txt(
    'Financiado ahora: suma de lo financiado que ya ocupa el cupo. Por financiar: lo de las exportaciones que todavía no lo ocupan. Por línea: lo financiado ahora + lo por financiar (cantidad de exportaciones).',
    'Financed now: sum of the financed amount already using the credit line. To be financed: that of exports not using it yet. By line: financed now + to be financed (number of exports).',
  ),
  buscarPagos: txt(
    'Busca en la exportación, el proveedor, la empresa que paga, el acuerdo y los comentarios.',
    'Searches the export, the supplier, the paying company, the agreement and the comments.',
  ),
  estadoPagos: txt(
    'Estado del pago. Sin elegir ninguno se ven los pendientes y solicitados.',
    'Payment status. With none chosen, pending and requested ones are shown.',
  ),
  lineaPagos: txt(
    'Línea con que se paga: la del pago o, si no tiene, la de su acuerdo.',
    'Line it is paid with: the payment’s own or, if none, its agreement’s.',
  ),
  soloVencidos: txt(
    'Solo pagos pendientes o solicitados cuya fecha meta ya pasó.',
    'Only pending or requested payments whose target date has passed.',
  ),
  metaDesde: txt('Solo pagos con fecha meta desde este día.', 'Only payments with a target date from this day.'),
  metaHasta: txt('Solo pagos con fecha meta hasta este día.', 'Only payments with a target date up to this day.'),
  totalesPagos: txt(
    'Pendiente: suma de los pagos filtrados pendientes o solicitados. Vencido: la parte de esos cuya fecha meta ya pasó. Pagado: suma de los ya pagados.',
    'Pending: sum of the filtered pending or requested payments. Overdue: the part of those whose target date has passed. Paid: sum of those already paid.',
  ),
  empresaGrafica: txt(
    'Empresa cuyas entradas y salidas previstas se ven en la gráfica.',
    'Company whose expected money in and out is shown in the chart.',
  ),
};

export const AYUDA_FORMULARIO_PAGO = {
  exportacion: txt(
    'Código de la exportación del pago; puede no estar todavía en la app.',
    'Code of the payment’s export; it may not be in the app yet.',
  ),
  tipo: txt('Anticipo, liquidación, ajuste, devolución…', 'Advance, settlement, adjustment, repayment…'),
  monto: txt(
    'Valor del pago en dólares; negativo si el proveedor devuelve dinero.',
    'Payment amount in dollars; negative if the supplier pays money back.',
  ),
  fechaMeta: txt(
    'Fecha en que debe hacerse el pago. Si el pago se registra o se edita en la app, ya no se mueve con la ETA a Mundra.',
    'Date the payment is due. If the payment is registered or edited in the app, it no longer moves with the Mundra ETA.',
  ),
  estado: txt(
    'Pendiente, Solicitado, Pagado o Cancelado. Un pago cancelado no cuenta en el Cashflow.',
    'Pending, Requested, Paid or Cancelled. A cancelled payment does not count in the Cashflow.',
  ),
  pagadoEl: txt(
    'Fecha en que se hizo el pago. Escríbela al marcarlo como Pagado.',
    'Date the payment was made. Enter it when marking it as Paid.',
  ),
  lineaEditar: txt(
    'Línea de financiación con que se paga. "La del acuerdo" usa la elegida en la pestaña Acuerdos.',
    'Financing line it is paid with. "From the agreement" uses the one chosen in the Agreements tab.',
  ),
  lineaNuevo: txt(
    'Línea de financiación con que se paga; sin línea no se descuenta de ningún cupo.',
    'Financing line it is paid with; without a line it is not deducted from any credit line.',
  ),
  comprador: txt(
    'Empresa del grupo que paga. Escribe su nombre o su código.',
    'Group company that pays. Enter its name or its code.',
  ),
  proveedor: txt('Proveedor tercero que recibe el pago.', 'Third-party supplier that receives the payment.'),
  comentarios: txt('Notas libres del pago.', 'Free notes about the payment.'),
};
